import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslationHandleService } from '../../../../core/translate/translation-handle.service';
import { Subscription } from 'rxjs/internal/Subscription';
import * as enums from '../../../sources/arcgis-security-types.enum';
import { CustomValidatorsService } from '../../../../shared/services/custom-validators.service';

const PHRASES_FOR_TRANSLATE = [
  {
    public: 'sources.details.Public',
  },
  {
    password: 'sources.details.Password',
  },
];

@Component({
  selector: 'mee-arcgis-custom-map-form',
  templateUrl: './arcgis-custom-map-form.component.html',
  styleUrls: ['./arcgis-custom-map-form.component.scss'],
})
export class ArcgisCustomMapFormComponent implements OnInit, OnDestroy {
  _isUpdateMode = false;
  arcGisCustomMapForm: UntypedFormGroup;
  securityTypes: Array<{ name: string; value: number }>;
  subs: Subscription[] = [];
  translationMap: Map<string, string>;
  @Input() mapServerUrlsOfCustomMapProviders: string[];
  @Input() selectedCustomMap: any; // ArcGisMapProvider
  @Output() arcGisDataChanged: EventEmitter<any> = new EventEmitter(); // <IArcGisMapProviderForm>
  @Input() set isUpdateMode(isUpdateMode) {
    this.arcGisDataChanged.next({ data: this.selectedCustomMap });
    this._isUpdateMode = isUpdateMode;
  }

  get isUpdateMode() {
    return this._isUpdateMode;
  }

  constructor(private translationHandleService: TranslationHandleService, private customValidatorsService: CustomValidatorsService) {
    translationHandleService.setPhrasesForTranslate(PHRASES_FOR_TRANSLATE);

    this.subs.push(
      translationHandleService.getTranslationMap().subscribe(translationMap => {
        this.translationMap = translationMap;
      }),
    );
  }

  ngOnInit() {
    this.initArcGisCustomMapForm();
    this.initSecurityTypes();
    this.onChangeFormValue();
    if (this.isUpdateMode) {
      this.arcGisCustomMapForm.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    }
  }

  initArcGisCustomMapForm() {
    this.arcGisCustomMapForm = new UntypedFormGroup({
      /*ArcGis map service*/

      securityType: new UntypedFormControl(this.isUpdateMode && this.selectedCustomMap.ArcGisUsername ? 1 : 0),
      mapServerUrl: new UntypedFormControl(this.isUpdateMode ? this.selectedCustomMap.MapServerUrl : ''),
      authUrl: new UntypedFormControl(this.isUpdateMode ? this.selectedCustomMap.AuthenticationUrl : ''),
      username: new UntypedFormControl(this.isUpdateMode ? this.selectedCustomMap.ArcGisUsername : ''),
      password: new UntypedFormControl(''),
      isArcGisOnline: new UntypedFormControl(this.isUpdateMode ? this.selectedCustomMap.IsArcGisOnline : false),
    });

    /*For form valid true on switch form between different map types*/

    if (!this.isUpdateMode) {
      this.arcGisCustomMapForm
        .get('mapServerUrl')
        .setValidators(this.customValidatorsService.onCheckUniqueString(this.mapServerUrlsOfCustomMapProviders));
      this.arcGisDataChanged.next({ data: {}, isFormValid: false });
    }
  }

  initSecurityTypes() {
    this.securityTypes = [
      {
        name: this.translationMap.get('public'),
        value: 0,
      },
      {
        name: this.translationMap.get('password'),
        value: 1,
      },
    ];
  }

  onChangeFormValue() {
    this.subs.push(
      this.arcGisCustomMapForm.valueChanges.subscribe(value => {
        let arcGisDataForCustomMap;

        if (this.isArcGisPassword()) {
          this.arcGisCustomMapForm.get('authUrl').setValidators([Validators.required]);
          this.arcGisCustomMapForm.get('username').setValidators([Validators.required]);

          if (!this.isUpdateMode) {
            this.arcGisCustomMapForm.get('password').setValidators([Validators.required]);
          }

          arcGisDataForCustomMap = {
            data: {
              MapServerUrl: value.mapServerUrl,
              AuthenticationUrl: value.authUrl,
              ArcGisUsername: value.username,
              ArcGisPassword: value.password,
              IsArcGisOnline: value.isArcGisOnline,
            },
          };
        } else {
          this.arcGisCustomMapForm.get('authUrl').clearValidators();
          this.arcGisCustomMapForm.get('username').clearValidators();
          this.arcGisCustomMapForm.get('password').clearValidators();

          arcGisDataForCustomMap = {
            data: {
              MapServerUrl: value.mapServerUrl,
              IsArcGisOnline: value.isArcGisOnline,
            },
          };
        }

        arcGisDataForCustomMap.isFormValid = this.arcGisCustomMapForm.valid;

        this.arcGisDataChanged.next(arcGisDataForCustomMap);
      }),
    );
  }

  isArcGisPassword() {
    return this.arcGisCustomMapForm.get('securityType').value === enums.ArcGisSecurityTypes.Password;
  }

  getControlRequiredError(controlName: string): boolean {
    const control = this.arcGisCustomMapForm.get(controlName);

    if (control === null) {
      return false;
    }

    return control.errors && control.errors.required && (control.dirty || control.touched);
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub: Subscription) => sub && sub.unsubscribe());
    this.translationHandleService.cleanTranslationArray();
  }
}
